import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HelpCircle, Search, Flame, Lightbulb, ThumbsUp, ThumbsDown, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { trFold, SearchClear } from "@/components/shared";
const FaqItem = ({ f, onVote, tidPrefix = "supplier-faq-item" }) => (
  <AccordionItem value={f.id} className="border-none bg-white px-4" data-testid={`${tidPrefix}-${f.id}`}>
    <AccordionTrigger className="text-left text-sm font-semibold hover:no-underline gap-3">
      <span className="flex items-center gap-2.5 text-red-600">
        <HelpCircle size={15} className="text-red-400 shrink-0" />
        {f.question}
      </span>
    </AccordionTrigger>
    <AccordionContent className="text-sm text-slate-600 pl-7">
      <div className="whitespace-pre-wrap">{f.answer}</div>
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100">
        <span className="text-xs text-slate-400">Bu cevap işinize yaradı mı?</span>
        <button
          type="button"
          onClick={() => onVote(f.id, true)}
          data-testid={`faq-vote-up-${f.id}`}
          className={`flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors duration-150 ${
            f.my_vote === true ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-slate-400"
          }`}
        >
          <ThumbsUp size={13} /> {f.up || 0}
        </button>
        <button
          type="button"
          onClick={() => onVote(f.id, false)}
          data-testid={`faq-vote-down-${f.id}`}
          className={`flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors duration-150 ${
            f.my_vote === false ? "border-red-400 bg-red-50 text-red-600" : "border-slate-200 text-slate-500 hover:border-slate-400"
          }`}
        >
          <ThumbsDown size={13} /> {f.down || 0}
        </button>
      </div>
    </AccordionContent>
  </AccordionItem>
);

export default function SupplierFaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [sugOpen, setSugOpen] = useState(false);
  const [sugText, setSugText] = useState("");
  const [sending, setSending] = useState(false);
  const counted = useRef(new Set());

  useEffect(() => {
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}).finally(() => setLoaded(true));
    api.get("/faq-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const onOpen = (id) => {
    if (!id || counted.current.has(id)) return;
    counted.current.add(id);
    api.post(`/faqs/${id}/view`).catch(() => {});
  };

  const vote = async (id, helpful) => {
    try {
      const { data } = await api.post(`/faqs/${id}/vote`, { helpful });
      setFaqs((fs) => fs.map((f) => (f.id === id ? { ...f, ...data } : f)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const q = trFold(search.trim());
  const filtered = q
    ? faqs.filter((f) => trFold(f.question).includes(q) || trFold(f.answer).includes(q))
    : faqs;

  const popular = [...faqs].filter((f) => (f.views || 0) > 0).sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 3);

  const catGroups = categories
    .map((c) => ({ id: c.id, name: c.name, items: faqs.filter((f) => f.category_id === c.id) }))
    .filter((g) => g.items.length > 0);
  const uncategorized = faqs.filter((f) => !f.category_id || !categories.some((c) => c.id === f.category_id));
  if (uncategorized.length > 0) catGroups.push({ id: "genel", name: "Genel", items: uncategorized });

  const openSuggest = (prefill) => { setSugText(prefill || ""); setSugOpen(true); };

  const sendSuggestion = async () => {
    if (sugText.trim().length < 5) {
      toast.error("Soru en az 5 karakter olmalıdır");
      return;
    }
    setSending(true);
    try {
      await api.post("/faqs/suggest", { question: sugText });
      toast.success("Öneriniz iletildi, teşekkürler!");
      setSugOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="supplier-faq-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">Yardım</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter">Sıkça Sorulan Sorular</h1>
        <p className="text-sm text-slate-500 mt-2">Portal kullanımı ve teklif süreçleriyle ilgili merak edilenler.</p>
      </div>

      {faqs.length > 0 && (
        <div className="relative mb-6">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Soru veya cevap içinde ara..."
            data-testid="faq-search-input"
            className="rounded-none border-slate-200 h-11 pl-9 pr-9 bg-white"
          />
          <SearchClear value={search} onClear={() => setSearch("")} testid="faq-search-clear" />
        </div>
      )}

      {!q && popular.length > 0 && (
        <div className="mb-8" data-testid="popular-faqs">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-bold flex items-center gap-1.5 mb-2">
            <Flame size={12} /> Popüler
          </div>
          <Accordion type="single" collapsible className="space-y-2" onValueChange={onOpen}>
            {popular.map((f) => <FaqItem key={`pop-${f.id}`} f={f} onVote={vote} tidPrefix="popular-faq-item" />)}
          </Accordion>
        </div>
      )}

      {faqs.length === 0 ? (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-500" data-testid="supplier-faq-empty">
          {loaded ? "Henüz soru eklenmedi." : "Yükleniyor..."}
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center" data-testid="faq-search-empty">
          <p className="text-sm text-slate-500 mb-4">"{search}" için sonuç bulunamadı.</p>
          <Button variant="outline" onClick={() => openSuggest(search.trim())} data-testid="suggest-faq-from-search" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 h-9 font-semibold transition-colors duration-200">
            <Lightbulb size={14} /> Bu soruyu adminlere önerin
          </Button>
        </div>
      ) : q ? (
        <Accordion type="single" collapsible className="space-y-2" onValueChange={onOpen} data-testid="faq-search-results">
          {filtered.map((f) => <FaqItem key={f.id} f={f} onVote={vote} />)}
        </Accordion>
      ) : (
        <Accordion type="multiple" className="space-y-3" data-testid="faq-categories-accordion">
          {catGroups.map((g) => (
            <AccordionItem key={g.id} value={`cat-${g.id}`} className="border border-slate-200 bg-white px-4" data-testid={`faq-cat-${g.id}`}>
              <AccordionTrigger className="text-left hover:no-underline gap-3">
                <span className="flex items-center gap-2.5 font-heading font-bold text-base tracking-tight">
                  <FolderOpen size={17} className="text-slate-400 shrink-0" />
                  {g.name}
                  <span className="text-xs text-slate-400 font-normal">({g.items.length} soru)</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                <Accordion type="single" collapsible className="space-y-2" onValueChange={onOpen}>
                  {g.items.map((f) => <FaqItem key={f.id} f={f} onVote={vote} />)}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {loaded && (
        <div className="mt-8 border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="text-sm text-slate-500">Aradığınız cevabı bulamadınız mı?</div>
          <Button variant="outline" onClick={() => openSuggest(search.trim())} data-testid="suggest-faq-button" className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 h-9 font-semibold transition-colors duration-200">
            <Lightbulb size={14} /> Soru Önerin
          </Button>
        </div>
      )}

      <Dialog open={sugOpen} onOpenChange={setSugOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Lightbulb size={18} /> Soru Önerin
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Öneriniz yöneticilere iletilir; uygun görülürse cevabıyla birlikte SSS'e eklenir.</p>
            <div className="space-y-1.5">
              <Label>Sorunuz</Label>
              <Textarea value={sugText} onChange={(e) => setSugText(e.target.value)} rows={3} placeholder="Örn: Teklif verdikten sonra dosya ekleyebilir miyim?" data-testid="suggest-question-input" className="rounded-none border-slate-200" />
            </div>
            <Button onClick={sendSuggestion} disabled={sending} data-testid="send-suggestion-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 w-full h-10 font-semibold transition-colors duration-200">
              {sending ? "Gönderiliyor..." : "Öneriyi Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
